import { IconComponent } from "@@common";
import { InputAdornment, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useErrorHandler } from "../../../common/hooks";
import useDebouncedCallback from "../../../common/hooks/useDebouncedCallback";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useComponentVisibility from "../../Hooks/useComponentVisibility";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { IconPlacement, InputFieldModel, InputFieldVariant } from "../../Models/InputFieldModel";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const variantMap: Record<InputFieldVariant, "standard" | "outlined" | "filled"> = {
  [InputFieldVariant.Standard]: "standard",
  [InputFieldVariant.Outlined]: "outlined",
  [InputFieldVariant.Filled]: "filled",
};

const InputFieldComponent: React.FC<IComponentProps<InputFieldModel>> = ({ component, context, componentPath = [] }) => {
  const errorHandler = useErrorHandler();

  const [value, setValue] = useState<string>("");
  const [valueResult] = useObservedExpression<string>(component.Value, context, "InputFieldComponent");
  const { isVisible, hiddenSx } = useComponentVisibility(component, context, "InputFieldComponent");

  useEffect(() => {
    if (valueResult !== value) {
      setValue(valueResult ?? "");
    }
  }, [valueResult]);

  // Debounced callback to update the context variable
  // This will recreate when debounceMs changes due to dependency array
  const debouncedUpdateContext = useDebouncedCallback((newValue: string) => {
    // Update the context variable if Value expression is provided
    if (component.Value) {
      try {
        context.Evaluator.assign(component.Value, context, newValue);
      } catch (error) {
        errorHandler(error);
      }
    }
  }, component.DebunceMs);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    // Update UI state immediately
    setValue(newValue);
    // Debounce the context update to avoid excessive evaluations
    debouncedUpdateContext(newValue);
  };

  const componentLayoutSx = getComponentLayout(component);

  const fieldName = component.Name ?? component.Id;

  const selectionProps = useComponentSelection({ component, componentPath });

  const variant = variantMap[component.Variant ?? InputFieldVariant.Standard];

  const hasIcon = Boolean(component.IconName);
  const iconPlacement = component.IconPlacement ?? IconPlacement.Start;
  const iconElement = hasIcon ? (
    <InputAdornment position={iconPlacement === IconPlacement.Start ? "start" : "end"}>
      <IconComponent iconName={component.IconName!} color={component.IconColor} />
    </InputAdornment>
  ) : undefined;

  if (!isVisible) return null;

  return (
    <TextField
      name={fieldName}
      label={component.Label}
      value={value}
      onChange={handleChange}
      placeholder={component.Placeholder}
      required={component.IsRequired}
      multiline={component.IsMultiLine}
      fullWidth
      variant={variant}
      sx={{ ...componentLayoutSx, ...hiddenSx }}
      slotProps={{
        input: {
          startAdornment: iconPlacement === IconPlacement.Start ? iconElement : undefined,
          endAdornment: iconPlacement === IconPlacement.End ? iconElement : undefined,
        },
      }}
      {...selectionProps}
    />
  );
};

export default InputFieldComponent;
